/**
 * Immozwerg – Homepage
 * HAJ Immo e.K. · Andrea Heinz · immozwerg.com
 */

import { useState, useEffect, useRef } from "react";
import { sendeAnGoogleSheets } from "@/lib/googleSheets";
import { Link } from "wouter";
import LeistungenKarussell from "@/components/LeistungenKarussell";
import { LOCALITY_PAGES } from "@shared/locality-pages";
import { ImmobilienKarte, useImmobilien } from "./Immobilien";
import { useImmobilienFavoriten } from "@/hooks/useImmobilienFavoriten";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import SEO, { schemaLocalBusiness } from "@/components/SEO";
import { useWhatsAppPopup } from "@/components/WhatsAppPopup";
import BlogCard from "@/components/BlogCard";
import SiteHeader from "@/components/SiteHeader";
import { allFAQs } from "@shared/faqs";
import { useLocale } from "@/contexts/LocaleContext";
import {
  Star,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Shield,
  Heart,
  Users,
  Award,
  Instagram,
  Facebook,
  Linkedin,
  Menu,
  X,
  Euro,
  Search,
  FileText,
  Handshake,
  MessageSquare,
  Home as HomeIcon,
} from "lucide-react";

// ─── Image URLs ───────────────────────────────────────────────────────────────
const HERO_IMG = "/assets/587a6bb9fbb5.webp";
// Echtes Profilfoto Andrea Heinz
const PORTRAIT_IMG = "/assets/7d098387821a.webp";
const INTERIOR_IMG =
  "/assets/9d19ff503383.webp";
const CITY_IMG =
  "/assets/d83025d53c6c.webp";
const KEYS_IMG =
  "/assets/298623ef38ef.webp";
// Immozwerg Avatar-Bilder (Maskottchen)
const ZWERG_WAVING = "/manus-storage/41aedcf5ae82_ffc32bc4.png";
const ZWERG_HOUSE_HAPPY = "/assets/f21a47e1d4e8.png";
const ZWERG_STANDING = "/assets/1e36ee8eab9c.png";
const ZWERG_RELAXED = "/assets/1112026a8a28.png";

// ─── Counter Hook ─────────────────────────────────────────────────────────────
function useCounter(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ─── Intersection Observer Hook ───────────────────────────────────────────────
function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// Immobilien werden dynamisch via useImmobilien-Hook geladen (Google Sheets)

// ─── Bewertungen (Google Maps, absolute Daten – neueste zuerst) ────────────
const reviews = [
  {
    name: "Florian K.",
    datum: "22. Juli 2026",
    stars: 5,
    text: "Bei Andrea fühlt man sich bestens aufgehoben. Die Beratung und Betreuung ist überragend. Von Besichtigung bis zum Notartermin hat alles reibungslos funktioniert. Vielen Dank!",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Clemens K.",
    datum: "22. Juli 2026",
    stars: 5,
    text: "Wirklich eine super Maklerin. Ich habe mit Andrea von der Besichtigung bis zur notariellen Beglaubigung des Kaufvertrags eine durch und durch positive Erfahrung gemacht.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Maurice S.",
    datum: "22. Juli 2026",
    stars: 5,
    text: "Zuverlässig, Kompetent und vor allem ein reibungsloser Ablauf. Frau Heinz war jederzeit erreichbar und sämtliche Anliegen konnten immer schnell und einfach abgesprochen und abgeklärt werden.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Judi R.",
    datum: "24. Juni 2026",
    stars: 5,
    text: "Wir arbeiten schon seit längerer Zeit mit Andrea zusammen und könnten nicht zufriedener sein. Sie begleitet uns rund um unsere Eigentumswohnung – egal ob es um sinnvolle Investitionen, Finanzierungsfragen oder wertsteigernde Maßnahmen geht. Was sie besonders auszeichnet: Sie denkt wirklich mit. Ihre Einschätzungen sind fundiert, ehrlich und immer darauf ausgerichtet, langfristig das Beste für uns herauszuholen. Man merkt sofort, dass hier nicht einfach verkauft wird, sondern echte Beratung stattfindet. Erreichbarkeit ist bei ihr kein Thema – sie ist praktisch immer da, nimmt sich Zeit und macht sogar Termine am Sonntag möglich, was heute alles andere als selbstverständlich ist. Dazu kommt ihre Art: unglaublich freundlich, aufmerksam, charmant und dabei gleichzeitig sehr kompetent und smart. Die Zusammenarbeit ist nicht nur effektiv, sondern macht auch menschlich einfach Spaß. Absolute Empfehlung – besser geht Immobilienberatung nicht.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Kaan I.",
    datum: "24. Mai 2026",
    stars: 5,
    text: "Ich freue mich sehr, dass ich Andrea beim Kauf meiner Immobilie als Maklerin an meiner Seite hatte. Zumal als Käufer dies nicht in meiner Macht stand und dies Glück war. Auf eine Antwort muss man bei Andrea nicht lange warten, und sie tut alles, was in ihrer Macht steht, um beide Parteien glücklich zu machen. Auch in schwierigen Situationen vermittelt Sie professionell. Bei Fragen war sie immer erreichbar. Und wenn sie einmal etwas nicht sofort wusste, hat sie die benötigten Informationen zuverlässig und schnell eingeholt. Bei ihr merkt man sofort, dass ihre Arbeit für sie nicht nur ein Beruf, sondern echte Leidenschaft ist. Auch Sonderwünsche im Notarvertrag sowie kurzfristige Termine hat sie möglich gemacht. Deshalb kann ich nur empfehlen, die Kontakte und Möglichkeiten, die Andrea mitbringt, dankend anzunehmen. Ich kann Sie wärmstens empfehlen.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Stefan S.",
    datum: "24. Mai 2026",
    stars: 5,
    text: "Andrea Heinz versteht es wirklich, auf die Bedürfnisse der involvierten Parteien einzugehen und hat das richtige Händchen bei der Immobilienvermittlung. Die Beratung, Vermarktung und Betreuung aus Verkäuferperspektive kann ich nur maximal positiv hervorheben, man fühlt sich rundum perfekt aufgehoben. Diese Art von Makler(in) findet man selten.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "S. von K.",
    datum: "14. September 2025",
    stars: 5,
    text: "Super Service: professionell, zuvorkommend, verbindlich!",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Tunca K.",
    datum: "14. September 2025",
    stars: 5,
    text: "Frau Heinz hat uns bei der Immobilienbesichtigung äußerst kompetent und engagiert beraten. Ihre sympathische Art, ihr fundiertes Fachwissen und die Kommunikation auf Augenhöhe haben uns sofort überzeugt. Besonders beeindruckt hat uns, mit wie viel Leidenschaft und Professionalität sie ihren Job führt. Wir fühlten uns zu jeder Zeit bestens betreut – uneingeschränkte Empfehlung!",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Franziska S.",
    datum: "14. September 2025",
    stars: 5,
    text: "Frau Heinz Ist eine nette emphatische und verständnisvolle Person. Sie hat Ihre Arbeit bei uns als Immobilien Maklerin sehr ernst genommen und zu unserer vollsten Zufriedenheit umgesetzt. Immer wieder gerne.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Sophie R.",
    datum: "24. Juli 2025",
    stars: 5,
    text: "Wir haben durch Andrea Heinz unser Eigenheim gefunden und sind darüber sehr glücklich. Sie hat das Haus ansprechend vermarktet, sodass wir direkt einen guten Eindruck gewinnen konnten. Besonders positiv hervorheben möchten wir Ihre sehr gute Erreichbarkeit, die schnelle und zuverlässige Beantwortung unserer Fragen sowie ihre freundliche Art. Vielen Dank für die angenehme Zusammenarbeit.",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Arthur R.",
    datum: "24. Juli 2025",
    stars: 5,
    text: "Super Service und super kompetent beraten von Andrea Heinz. Kann ich nur empfehlen",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
  {
    name: "Sven B.-G.",
    datum: "24. Juli 2025",
    stars: 5,
    text: "Ich habe nur positives zu berichten. Das Büro ist klasse, freundlich und es hat alles super geklappt. Ich bin sehr zufrieden. Frau Heinz möchte ich auch nochmals sehr hervorheben. Sie war es die über eine Stunde auf mich gewartet hat. Ohne sie wäre der Deal nicht zustande gekommen. Vielen herzlichen Dank dafür. Ich bin sehr dankbar. Liebe Grüße Sven",
    quelle: "Google Maps",
    link: "https://www.google.com/maps/place/HAJ+Immo+e.K/@49.0702822,9.0080288,17z/data=!4m8!3m7!1s0x4df1e71cba5168b9:0xfc68224396f52bbf!8m2!3d49.0702822!4d9.0080288!9m1!1b1!16s%2Fg%2F11ltpx8020?hl=de",
  },
];

// ─── FAQ (Teaser für Homepage, verlinkt zur vollständigen FAQ-Unterseite) ───
const faqs = allFAQs.slice(0, 5).map(f => ({
  q: f.q,
  a: f.a.length > 140 ? f.a.substring(0, 137) + "..." : f.a
}));

// ─── Prozessschritte ──────────────────────────────────────────────────────────
const processSteps = {
  eigentuemer: [
    { n: "01", icon: <Phone size={22} />, title: "Erstgespräch", desc: "Kostenlos und unverbindlich – in Brackenheim, Güglingen, Heilbronn oder online. Ich lerne Ihre Immobilie und Ihre Situation kennen" },
    { n: "02", icon: <TrendingUp size={22} />, title: "Bewertung", desc: "Marktgerechte Preisanalyse auf Basis aktueller Vergleichsdaten aus dem Zabergäu, Brackenheim und Heilbronn" },
    { n: "03", icon: <Users size={22} />, title: "Ihre Leistungsauswahl", desc: "Full-Service, gezielte Einzelleistungen oder Begleitung beim Selbstverkauf – Sie entscheiden, wie viel Unterstützung Sie möchten" },
    { n: "04", icon: <Handshake size={22} />, title: "Abschluss", desc: "Notartermin, Übergabe, vollständige Unterlagen – ich begleite Sie bis zum Ende" },
  ],
  kaeufer: [
    { n: "01", icon: <MessageSquare size={22} />, title: "Suchprofil", desc: "Wir klären Ihre Wünsche, Ihr Budget und Ihre Zeitvorstellung" },
    { n: "02", icon: <Search size={22} />, title: "Objektauswahl", desc: "Ich suche für Sie – im Netzwerk, auf Portalen und vor Aufschaltung" },
    { n: "03", icon: <HomeIcon size={22} />, title: "Besichtigung & Prüfung", desc: "Kaufpreiseinschätzung, Unterlagenprüfung, Finanzierungs- und Fördercheck" },
    { n: "04", icon: <FileText size={22} />, title: "Abschluss", desc: "Kaufvertragsprüfung, Notartermin – ich begleite Sie bis zum Einzug" },
  ],
};

// ─── Immobilien-Sektion für Homepage (3 neueste Objekte) ─────────────────────────────────────────────────────────────────────────────────
function ImmobilienHomepageSection() {
  const { data, loading } = useImmobilien();
  const { istFavorit, toggle: toggleFavorit } = useImmobilienFavoriten();
  const preview = [...data].reverse().slice(0, 3);
  return (
    <section id="immobilien" className="py-20 bg-[#FAF8F5]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#8a6a20" }}>Aktuelle Angebote</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#2d1b4e]" style={{ fontFamily: "'Playfair Display', serif" }}>Meine Immobilien</h2>
          </div>
          <Link href="/immobilien" aria-label="Alle Immobilien ansehen">
            <span className="hidden sm:flex items-center gap-1 text-sm font-bold hover:gap-2 transition-all cursor-pointer" style={{ color: "#4a2d7a" }}>
              Alle Immobilien ansehen <ArrowRight size={14} />
            </span>
          </Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-[#a066c2] border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {preview.map(obj => (
              <ImmobilienKarte
                key={obj.id}
                obj={obj}
                isFavorit={istFavorit(obj.id)}
                onToggleFavorit={toggleFavorit}
              />
            ))}
          </div>
        )}
        <div className="text-center mt-8">
          <Link href="/immobilien">
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white cursor-pointer transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
              Alle Immobilien ansehen <ArrowRight size={15} />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}



// ─── Hauptkomponente ─────────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState<"eigentuemer" | "kaeufer">("eigentuemer");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { openWhatsApp, popupElement } = useWhatsAppPopup();

  // Kontakt-System
  const [contactPanel, setContactPanel] = useState<"none" | "form" | "direct">("none");
  const [contactChannel, setContactChannel] = useState<"" | "whatsapp" | "telefon" | "mail">("mail");
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "", thema: "", type: "eigentuemer", kontaktwunsch: "andrea" });
  const [formSent, setFormSent] = useState(false);
  const [directChannel, setDirectChannel] = useState<"" | "whatsapp" | "telefon" | "mail">("whatsapp");
  const [directForm, setDirectForm] = useState({ name: "", contact: "", message: "" });
  const [directSent, setDirectSent] = useState(false);

  const statsRef = useInView(0.3);
  const sold = useCounter(250, 1800, statsRef.inView);
  const years = useCounter(6, 1200, statsRef.inView);
  const rating = useCounter(100, 1500, statsRef.inView);
  const clients = useCounter(150, 2000, statsRef.inView);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    sendeAnGoogleSheets({
      name: contactForm.name,
      email: contactForm.email,
      telefon: contactForm.phone,
      anfragetyp: contactForm.thema,
      nachricht: contactForm.message,
      kontaktwunsch: "Ja – bitte melden",
      kommunikationsweg: contactChannel === "whatsapp" ? "WhatsApp" : contactChannel === "telefon" ? "Telefon" : "E-Mail",
      quelle: "Kontaktformular (Startseite)",
      kontaktErlaubt: "Ja",
    });
    setFormSent(true);
  };

  const handleDirectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const kanal = directChannel === "whatsapp" ? "WhatsApp" : directChannel === "telefon" ? "Telefon" : "E-Mail";
    sendeAnGoogleSheets({
      name: directForm.name,
      email: directChannel === "mail" ? directForm.contact : "",
      telefon: directChannel !== "mail" ? directForm.contact : "",
      nachricht: directForm.message,
      kontaktwunsch: "Nein – ich melde mich selbst",
      kommunikationsweg: kanal,
      quelle: "Direktkontakt (Startseite)",
      kontaktErlaubt: "Nein",
    });
    if (directChannel === "whatsapp") {
      const txt = `Hallo Andrea!\n\n👤 *Name:* ${directForm.name}\n\n💬 ${directForm.message}`;
      window.open(`https://wa.me/491738660756?text=${encodeURIComponent(txt)}`, "_blank");
    } else if (directChannel === "telefon") {
      window.location.href = "tel:+491738660756";
    } else {
      window.location.href = `mailto:hallo@haj-immokg.com?subject=Anfrage%20von%20${encodeURIComponent(directForm.name)}&body=${encodeURIComponent(directForm.message)}`;
    }
    setDirectSent(true);
  };

  const toggleContactPanel = (panel: "form" | "direct") => {
    setContactPanel(prev => prev === panel ? "none" : panel);
    setFormSent(false);
    setDirectSent(false);
  };

  return (
    <>
    {popupElement}
    <div className="min-h-screen bg-[#FAF8F5] text-[#1a1228]" style={{ fontFamily: "'Lato', sans-serif" }}>
      <SEO
        title="Immobilienmaklerin in Brackenheim, Güglingen & Heilbronn"
        description="Andrea Heinz begleitet Immobilienverkäufe und Bewertungen in Brackenheim, Güglingen, Heilbronn, Lauffen am Neckar, Schwaigern, Leingarten und im Zabergäu. Persönlich, transparent, unverbindlich."
        canonical="/"
        schema={[schemaLocalBusiness]}

      />

      <SiteHeader />

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section
        id="site-hero"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ minHeight: "100vh", position: "relative", display: "flex", alignItems: "center", overflow: "hidden" }}
      >
        <div className="absolute inset-0" data-hero-background>
          <picture>
            <source media="(max-width: 639px)" srcSet="/assets/8e5d5fc18781.webp" type="image/webp" />
            <source media="(max-width: 767px)" srcSet="/assets/1a986362d76d.webp" type="image/webp" />
            <img src={HERO_IMG} alt="Haus in Güglingen – Immobilienmaklerin Andrea Heinz Immozwerg" className="w-full h-full object-cover" width="1920" height="1080" fetchPriority="high" loading="eager" decoding="async" />
          </picture>
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(20,10,40,0.88) 0%, rgba(20,10,40,0.60) 55%, rgba(20,10,40,0.25) 100%)" }} />
        </div>
        <div data-hero-content className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20">

          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 border border-yellow-300/40 bg-yellow-300/10">
              <MapPin size={14} className="text-yellow-300" />
              <span className="text-yellow-200 text-sm font-medium tracking-wide">Brackenheim · Güglingen · Heilbronn · Zabergäu</span>
            </div>
            <h1 className="text-sm font-semibold tracking-[0.18em] uppercase text-yellow-200/80 mb-3">Immobilienmaklerin in Güglingen, Brackenheim &amp; Heilbronn – Andrea Heinz</h1>
            <p className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Klein im Namen.<br />
              <span style={{ color: "#f0d080", fontStyle: "italic" }}>Groß</span> im Einsatz
            </p>
            <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-xl">
              Ich bin <strong className="text-white">Andrea Heinz</strong> – Immobilienmaklerin aus Güglingen mit 6 Jahren Leidenschaft für Menschen und Immobilien. Transparent, direkt – und ein „Das geht nicht" kenne ich nicht.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              {[
                { label: "Wertermittlung", href: "/#leistungen", tip: "Kostenlose Marktpreisanalyse Ihrer Immobilie" },
                { label: "Exposé & Fotos", href: "/#leistungen", tip: "Professionelles Exposé mit Fototermin" },
                { label: "Dokumente & Unterlagen", href: "/#leistungen", tip: "Beschaffung und Prüfung aller nötigen Unterlagen" },
                { label: "Notar & Kaufvertrag", href: "/#leistungen", tip: "Notarbeauftragung und Kaufvertragsprüfung" },
                { label: "Finanzierung", href: "/finanzierung", tip: "Finanzierungsberatung & Förderprogramme" },
                { label: "Erbschaft", href: "/erbschaft", tip: "Begleitung bei Erbimmobilien" },
                { label: "Grundrisse", href: "/#leistungen", tip: "Aufbereitung & verkaufsfördernde Darstellung" },
              ].map((pill) => (
                <a key={pill.label} href={pill.href} title={pill.tip}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm hover:bg-white/20 hover:border-white/40 transition-all duration-200 cursor-pointer">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c]" />
                  {pill.label}
                </a>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <button onClick={() => scrollTo("bewertung")}
                className="px-7 py-4 rounded-full font-bold text-base transition-all duration-200 hover:scale-105 hover:shadow-xl"
                style={{ background: "linear-gradient(135deg, #c9a84c, #e8c96a)", color: "#1a1228" }}>
                Kostenlose Bewertung
              </button>
              <button onClick={() => scrollTo("kontakt")}
                className="px-7 py-4 rounded-full font-bold text-base border-2 border-white/50 text-white hover:bg-white/10 transition-all duration-200">
                Jetzt Kontakt aufnehmen
              </button>
            </div>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-white/80">
                <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />)}</div>
                <span className="text-sm">5.0 Google (15 Bewertungen)</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <CheckCircle size={14} className="text-green-400" />
                <span className="text-sm">Kostenlose Erstberatung</span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown size={28} className="text-white/60" />
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────────────────────────── */}
      <section ref={statsRef.ref} className="py-12" style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: sold, fallback: 250, suffix: "+", label: "Kunden beim Notar" },
              { value: years, fallback: 6, suffix: " Jahre", label: "Erfahrung" },
              { value: rating, fallback: 100, suffix: "%", label: "Weiterempfehlungsrate" },
              { value: clients, fallback: 120, suffix: "+", label: "Exposés erstellt" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: "#f0d080" }}>
                  <span aria-label={`${stat.fallback}${stat.suffix}`}>{stat.value || stat.fallback}{stat.suffix}</span>
                </div>
                <div className="text-white/70 text-sm tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEISTUNGEN KARUSSELL ──────────────────────────────────────────── */}
      <LeistungenKarussell scrollTo={scrollTo} />

      {/* ── LEISTUNGEN PROZESS (Tab-Switcher) ─────────────────────────────── */}
      <section id="leistungen" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Tab Switcher für Zielgruppen */}
          <div className="border-t border-gray-100 pt-14">
            <div className="text-center mb-10">
              <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#8a6a20" }}>Ihr Weg – Schritt für Schritt</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#2d1b4e]" style={{ fontFamily: "'Playfair Display', serif" }}>
                So läuft es ab
              </h2>
            </div>
            <div className="flex justify-center mb-10">
              <div className="flex bg-[#FAF8F5] rounded-full p-1.5 gap-1">
                <button onClick={() => setActiveTab("eigentuemer")}
                  className={`px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 ${activeTab === "eigentuemer" ? "text-white shadow-md" : "text-[#2d1b4e] hover:text-[#4a2d7a]"}`}
                  style={activeTab === "eigentuemer" ? { background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)" } : {}}>
                  Ich bin Eigentümer
                </button>
                <button onClick={() => setActiveTab("kaeufer")}
                  className={`px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 ${activeTab === "kaeufer" ? "text-white shadow-md" : "text-[#2d1b4e] hover:text-[#4a2d7a]"}`}
                  style={activeTab === "kaeufer" ? { background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)" } : {}}>
                  Ich suche eine Immobilie
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps[activeTab].map((step, i) => (
                <div key={i} className="relative">
                  {i < 3 && <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 z-0" style={{ background: "linear-gradient(to right, #4a2d7a40, transparent)" }} />}
                  <div className="relative z-10 bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow text-center">
                    <div className="text-xs font-bold tracking-[0.15em] uppercase mb-3" style={{ color: "#8a6a20" }}>{step.n}</div>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)", color: "#f0d080" }}>
                      {step.icon}
                    </div>
                    <h3 className="font-bold text-[#2d1b4e] mb-2 text-base">{step.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── IMMOBILIEN ─────────────────────────────────────────────────────── */}
      <ImmobilienHomepageSection />

      {/* ── ÜBER MICH (Teaser → Link zur Unterseite) ──────────────────────── */}
      <section id="ueber-mich" className="py-20 overflow-hidden bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-48 h-48 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #4a2d7a, transparent)" }} />
              <img src={PORTRAIT_IMG} alt="Andrea Heinz – Immobilienmaklerin Güglingen, Heilbronn, Stuttgart"
                className="w-full max-w-sm mx-auto lg:mx-0 h-[480px] object-cover object-top rounded-2xl shadow-2xl"
                width="800" height="1067" loading="lazy" decoding="async" sizes="(max-width: 1024px) 100vw, 384px" />
              {/* Immozwerg winkend – kleines Overlay unten links */}

              <div className="absolute -bottom-6 -right-4 lg:-right-10 bg-white rounded-2xl p-5 shadow-xl border border-gray-100 max-w-[200px]">
                <div className="flex mb-2">{[1,2,3,4,5].map(i => <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />)}</div>
                <div className="text-sm font-bold text-[#2d1b4e]">5.0 Google</div>
                <div className="text-xs text-gray-500">15 Bewertungen</div>
              </div>
            </div>
            <div>
              <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#8a6a20" }}>Über mich</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#2d1b4e] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Andrea Heinz –<br />
                <span className="italic">Tacheles statt Kuschelkurs</span>
              </h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Von Radolfzell über Kreta bis nach Güglingen – mein Weg war alles andere als geradlinig. Aber genau das macht mich aus: Ich kenne das Leben aus verschiedenen Perspektiven und bringe diese Erfahrung in jede Beratung mit.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Ich sage Ihnen die Wahrheit – auch wenn sie manchmal unbequem ist. Keine leeren Versprechen, keine überhöhten Preisvorstellungen. Dafür echte Ergebnisse und eine Zusammenarbeit auf Augenhöhe.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: <Heart size={16} />, label: "Mit Herz" },
                  { icon: <Award size={16} />, label: "IHK-zertifiziert" },
                  { icon: <Shield size={16} />, label: "Transparent" },
                  { icon: <MapPin size={16} />, label: "Regional verwurzelt" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-[#FAF8F5] rounded-xl p-3 border border-gray-100">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)", color: "#f0d080" }}>
                      {item.icon}
                    </div>
                    <span className="font-medium text-[#2d1b4e] text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Link href="/ueber-mich">
                  <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-white cursor-pointer transition-all hover:scale-105"
                    style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)" }}>
                    Meine Geschichte lesen <ArrowRight size={14} />
                  </div>
                </Link>
                <div className="flex gap-3">
                  <a href="https://www.instagram.com/immozwerg" target="_blank" rel="noopener noreferrer"
                    aria-label="Instagram öffnen"
                    className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    style={{ background: "linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)", color: "#fff" }}>
                    <Instagram size={18} />
                  </a>
                  <a href="https://www.facebook.com/profile.php?id=61572975815321" target="_blank" rel="noopener noreferrer"
                    aria-label="Facebook öffnen"
                    className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)", color: "#fff" }}>
                    <Facebook size={18} />
                  </a>
                  <a href="https://www.linkedin.com/in/andrea-heinz-822596145/" target="_blank" rel="noopener noreferrer"
                    aria-label="LinkedIn öffnen"
                    className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)", color: "#fff" }}>
                    <Linkedin size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── REGION ─────────────────────────────────────────────────────────── */}
      <section id="einsatzgebiet" className="relative overflow-hidden py-14 sm:py-16">
        <div className="absolute inset-0">
          <img src={CITY_IMG} alt="Heilbronn – Immobilien Region Heilbronn Stuttgart" className="w-full h-full object-cover" width="1600" height="900" loading="lazy" decoding="async" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,10,40,0.7), rgba(20,10,40,0.85))" }} />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em]" style={{ color: "#f0d080" }}>Meine Region</p>
          <h2 className="text-3xl font-bold text-white sm:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>Dein Umzug</h2>
          <p className="mx-auto mb-6 mt-3 max-w-2xl text-base leading-relaxed text-white/80">Entdecke die Orte der Region und finde heraus, was zu deinem Alltag passt.</p>
          <div className="mx-auto flex max-w-5xl gap-2 overflow-x-auto pb-3 text-left [scrollbar-width:thin] sm:justify-center">
            {LOCALITY_PAGES.map((page) => (
              <Link key={page.slug} href={`/immobilienmakler/${page.slug}`} aria-label={`Immobilienmakler ${page.city} – Ortsseite ansehen`} className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-bold text-white backdrop-blur-sm transition hover:border-[#f0d080] hover:bg-white hover:text-[#2d1b4e]">
                Immobilienmakler {page.city}<ArrowRight size={15} className="shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <Link href="/umziehen-in-heilbronn" className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#f0d080] px-5 py-3 text-sm font-bold text-[#2d1b4e] transition hover:bg-white">Alle Ortsprofile & Umzugsleitfaden <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </section>

      {/* ── BEWERTUNGEN – schmaler Streifen, 3 Karten nebeneinander ─────────── */}
      <section id="bewertungen" className="py-10 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Kopfzeile kompakt */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />)}</div>
              <span className="font-bold text-[#2d1b4e] text-base">5,0</span>
              <span className="text-gray-500 text-sm">· 15 Google-Bewertungen</span>
            </div>
            <a
              href="https://www.google.com/search?q=HAJ+IMMO+e.K.+Immozwerg+G%C3%BCglingen&ludocid=18190064261748769727#lrd=0x47970a5c3d5c2a3d:0xfc68224396f52bbf,1"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#631172] font-semibold hover:text-[#a066c2] transition-colors flex items-center gap-1"
            >
              Alle Bewertungen auf Google Maps
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          </div>
          {/* 4 Karten nebeneinander – nur Vorschau (Text auf 3 Zeilen gekürzt) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {reviews.slice(0, 4).map((r, i) => (
              <a
                key={i}
                href={r.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#FAF8F5] rounded-xl p-4 border border-[#ede5f5] hover:shadow-md hover:border-[#c9a0e0] transition-all group"
              >
                <div className="flex items-center justify-between mb-2" data-original-content>
                  <span className="font-bold text-[#2d1b4e] text-sm">{r.name}</span>
                  <span className="text-[10px] text-gray-500">{r.datum}</span>
                </div>
                <div className="flex gap-0.5 mb-2">
                  {Array.from({ length: r.stars }).map((_, s) => (
                    <Star key={s} size={12} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-500 text-xs leading-relaxed line-clamp-3" data-original-content>{r.text}</p>
                <p className="text-[10px] text-gray-500 mt-2" data-original-content>Quelle: {r.quelle}</p>
              </a>
            ))}
          </div>
          {/* CTA Button für Google-Bewertung */}
          <div className="flex justify-center mt-8">
            <a
              href="https://goo.gl/maps/eJsCy5NgwiCUUhdE8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white transition-all hover:scale-105 hover:shadow-lg"
              style={{ background: "#631172" }}
            >
              ⭐ Jetzt bewerten
            </a>
          </div>
        </div>
      </section>

      {/* ── IMMOBILIENBEWERTUNG ───────────────────────────────────────────── */}
      <section id="wertrechner" className="bg-[#FAF8F5] py-14 lg:py-16" aria-labelledby="bewertung-teaser-title">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#8a6a20]">Kostenlos & unverbindlich</p>
          <h2 id="bewertung-teaser-title" className="text-3xl font-bold text-[#2d1b4e] lg:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>Was ist Ihre Immobilie wert?</h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-gray-600">Erhalten Sie in wenigen Schritten eine erste Werteinschätzung für Ihre Immobilie in Güglingen, Brackenheim, Heilbronn und dem Zabergäu.</p>
          <Link href="/immobilienbewertung">
            <span className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-full px-7 py-3 font-bold text-white transition-transform hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
              Jetzt Immobilie bewerten <ArrowRight size={16} />
            </span>
          </Link>
        </div>
      </section>



      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <section id="faq" className="py-20 bg-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#8a6a20" }}>FAQ</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#2d1b4e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Häufige Fragen
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors">
                  <span className="font-bold text-[#2d1b4e] pr-4">{faq.q}</span>
                  <ChevronDown size={18} className={`flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} style={{ color: "#4a2d7a" }} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-gray-600 text-sm leading-relaxed border-t border-gray-50 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link href="/faq">
              <span className="inline-flex items-center gap-2 text-sm font-bold hover:gap-3 transition-all cursor-pointer" style={{ color: "#4a2d7a" }}>
                Alle Fragen ansehen <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── BLOG VORSCHAU STREIFEN ─────────────────────────────────────── */}
      <BlogVorschauStreifen />

      {/* ── KONTAKT CTA ──────────────────────────────────────────────────── */}
      <section id="kontakt" className="py-20 bg-[#FAF8F5] text-center border-t border-[#e6dcd2]">
        <div className="max-w-xl mx-auto px-4">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#8a6a20" }}>Kontakt</p>
          <h2 className="text-4xl font-bold text-[#2d1b4e] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Lass uns <span className="italic">reden.</span>
          </h2>
          <p className="text-gray-500 mb-8">Hinterlassen Sie mir Ihre Kontaktdaten – ich melde mich auf dem Weg, der Ihnen am liebsten ist.</p>
          <Link href="/kontakt">
            <button className="px-10 py-4 rounded-full font-bold text-white text-base transition-all hover:scale-105 hover:shadow-xl"
              style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
              Kontakt aufnehmen
            </button>
          </Link>
        </div>
      </section>

    </div>
    </>
  );
}

// ─── Blog Vorschau Streifen (Startseite) ─────────────────────────────────────
function BlogVorschauStreifen() {
  const { posts, loading } = useBlogPosts();
  const neueste = posts.slice(0, 3);

  if (loading) return null;
  if (neueste.length === 0) return null;

  return (
    <section className="py-12 bg-[#f5f0fa] border-y border-[#e6dcd2]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Kopfzeile */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase mb-1" style={{ color: "#765312" }}>Aktuell</p>
            <h2 className="text-xl font-bold text-[#2d1b4e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Neues aus dem Immobilien-Blog
            </h2>
          </div>
          <Link href="/blog">
            <span className="text-sm font-semibold text-[#631172] hover:text-[#a066c2] transition-colors flex items-center gap-1 cursor-pointer">
              Alle Beiträge
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </span>
          </Link>
        </div>

        {/* 3 Karten nebeneinander mit exakt derselben BlogCard Komponente */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {neueste.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </div>
      </div>
    </section>
  );
}
